import Toast from "@/components/base/Toast";
import { usePhoneMask } from "@/hooks/usePhoneMask";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { analytics, site } from "@/content";
import { Rich } from "@/lib/rich";
import { useAnalyticsLeadForm } from "../hooks/useAnalyticsLeadForm";

export default function AnalyticsContactForm() {
  const {
    form,
    loading,
    charCount,
    setCharCount,
    managers,
    setManagers,
    agreed,
    setAgreed,
    submitted,
    setSubmitted,
    toast,
    setToast,
    handleSubmit,
  } = useAnalyticsLeadForm();

  const phoneMask = usePhoneMask();
  const { ref: headerRef, revealed: headerRevealed } = useScrollReveal();

  const socials = site.socials.filter((social) =>
    ["telegram-support", "vk", "rutube"].includes(social.key)
  );

  return (
    <section id="contact-form" className="py-24 bg-slate-50 dark:bg-[#0C0C0E] relative overflow-hidden">
      <div className="absolute top-[5%] right-[0%] w-[400px] h-[400px] rounded-full bg-blue-100/40 dark:bg-blue-500/[0.12] blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[10%] left-[-5%] w-[300px] h-[300px] rounded-full bg-slate-200/30 dark:bg-white/[0.04] blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Левая часть */}
          <div
            ref={headerRef}
            className={`flex-1 text-center lg:text-left transition-all duration-700 ${
              headerRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/[0.12] border border-blue-100 dark:border-blue-400/25 text-blue-700 dark:text-blue-400 text-xs font-semibold tracking-widest uppercase mb-6">
              Бесплатная консультация
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight mb-6">
              <Rich text={analytics.contactForm.title} accentClassName="text-blue-600 dark:text-blue-400" />
            </h2>
            <p className="text-slate-600 dark:text-white/65 text-lg leading-relaxed mb-8 max-w-md mx-auto lg:mx-0">
              Оставьте заявку — подключим демо-доступ за 15 минут и покажем, как
              выглядит настоящая аналитика. Без обязательств и предоплаты.
            </p>

            <div className="flex flex-col gap-5 text-slate-700 dark:text-white/80 text-sm mb-8">
              {(form.bullets ?? []).map((bullet) => (
                <div
                  key={bullet.text}
                  className="flex items-center gap-3 justify-center lg:justify-start"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-500/[0.12] shrink-0">
                    <i className={`${bullet.icon} text-blue-600 dark:text-blue-400 text-sm`}></i>
                  </div>
                  <span>{bullet.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 text-slate-500 dark:text-white/60 text-sm">
              <a
                href={site.contacts.emailHref}
                className="flex items-center gap-2 justify-center lg:justify-start hover:text-slate-700 transition-colors cursor-pointer"
              >
                <i className="ri-mail-line text-sm"></i>
                {site.contacts.email}
              </a>
              <a
                href={site.contacts.phoneHref}
                className="flex items-center gap-2 justify-center lg:justify-start hover:text-slate-700 transition-colors cursor-pointer"
              >
                <i className="ri-phone-line text-sm"></i>
                {site.contacts.phone}
              </a>
            </div>

            <div className="flex gap-3 mt-6 justify-center lg:justify-start">
              {socials.map((social) => (
                <a
                  key={social.key}
                  href={social.href}
                  aria-label={social.label}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.09] text-slate-500 dark:text-white/60 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all cursor-pointer shadow-sm"
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Форма */}
          <div className="flex-1 w-full max-w-lg">
            <div className="bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.09] rounded-3xl p-8 md:p-10 shadow-md">
              <div className="text-center mb-6">
                <div className="text-slate-900 dark:text-white font-bold text-lg mb-1">
                  {form.title}
                </div>
                <div className="text-slate-500 dark:text-white/60 text-sm">{form.subtitle}</div>
              </div>

              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-5">
                    <i className="ri-check-line text-emerald-600 text-2xl"></i>
                  </div>
                  <div className="text-slate-900 dark:text-white font-bold text-lg mb-2">
                    {form.successTitle}
                  </div>
                  <p className="text-slate-600 dark:text-white/65 text-sm leading-relaxed">
                    {form.successText}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-5 text-sm text-blue-700 dark:text-blue-400 hover:text-blue-600 font-semibold transition-colors cursor-pointer"
                  >
                    {form.successAgain}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-slate-500 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                      {form.fields.name}
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder={form.fields.namePlaceholder}
                      className="w-full bg-white dark:bg-white/[0.04] border border-slate-300 dark:border-white/[0.14] rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-500 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                      {form.fields.phone}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder={form.fields.phonePlaceholder}
                      {...phoneMask}
                      className="w-full bg-white dark:bg-white/[0.04] border border-slate-300 dark:border-white/[0.14] rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-500 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                      {form.fields.crm}
                    </label>
                    <select
                      name="crm"
                      defaultValue=""
                      className="w-full bg-white dark:bg-white/[0.04] border border-slate-300 dark:border-white/[0.14] rounded-xl px-4 py-3 text-slate-900 dark:text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all appearance-none cursor-pointer"
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 16px center",
                        paddingRight: "40px",
                      }}
                    >
                      {form.crmOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-500 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                      {form.fields.managers}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {form.managerOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setManagers(option.value)}
                          aria-pressed={managers === option.value}
                          className={`py-2.5 px-3 rounded-xl text-sm font-semibold transition-all cursor-pointer border ${
                            managers === option.value
                              ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                              : "bg-white dark:bg-white/[0.04] text-slate-600 dark:text-white/65 border-slate-300 dark:border-white/[0.14] hover:border-blue-400 hover:text-blue-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <input type="hidden" name="managers_count" value={managers} />
                  </div>

                  <div>
                    <label className="block text-slate-500 dark:text-white/60 text-xs font-semibold mb-2 uppercase tracking-wide">
                      {form.fields.message}
                    </label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      placeholder={form.fields.messagePlaceholder}
                      onChange={(event) => setCharCount(event.target.value.length)}
                      className="w-full bg-white dark:bg-white/[0.04] border border-slate-300 dark:border-white/[0.14] rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder-slate-400 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                    />
                    <div className="text-right text-slate-500 dark:text-white/60 text-xs mt-1">
                      {charCount}/500
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(event) => setAgreed(event.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-slate-300 dark:border-white/[0.14] text-blue-600 dark:text-blue-400 focus:ring-blue-500 focus:ring-2 cursor-pointer"
                    />
                    <span className="text-slate-500 dark:text-white/60 text-xs leading-relaxed">
                      <ConsentText label={form.consentLabel} />
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={loading || charCount > 500 || !agreed}
                    className="w-full py-4 rounded-2xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-blue-200"
                  >
                    {loading ? form.submittingLabel : form.submitLabel}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        visible={toast.visible}
        onClose={() => setToast((state) => ({ ...state, visible: false }))}
      />
    </section>
  );
}

/** Согласие с политикой: акцентный фрагмент становится ссылкой */
function ConsentText({ label }: { label: string }) {
  const parts = label.split("**");
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <a
            key={index}
            href={site.policyUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-700 dark:text-blue-400 hover:text-blue-600 underline"
          >
            {part}
          </a>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </>
  );
}
